import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { PersonSettingsDocument, SystemSettingsDocument, UpdatePersonSettingsDocument } from "@/graphql/generated/operations";

// The admin-authored welcome message appears twice on Home: first as a dialog the
// user sits through once, then as the hint block that stays until dismissed. Both
// read the same text and both remember "seen" per message text in the person's
// settings, so a changed message re-triggers each of them exactly once.
const DISMISSED_KEY = "dismissedWelcomeMessage";
const ACKNOWLEDGED_KEY = "acknowledgedWelcomeMessage";

export function useWelcomeMessage() {
  const [dismissedLocally, setDismissedLocally] = useState(false);
  const [acknowledgedLocally, setAcknowledgedLocally] = useState(false);
  const { data: systemSettingsData } = useQuery(SystemSettingsDocument);
  const { data: personSettingsData, loading: personSettingsLoading, refetch: refetchPersonSettings } =
    useQuery(PersonSettingsDocument);
  const [updatePersonSettings] = useMutation(UpdatePersonSettingsDocument);

  const message = systemSettingsData?.systemSettings?.welcomeMessage?.trim() ?? "";
  const personSettings = personSettingsData?.settings.settings ?? {};

  const hintVisible =
    message.length > 0 &&
    !personSettingsLoading &&
    personSettings[DISMISSED_KEY] !== message &&
    !dismissedLocally;
  // A dismissed hint means the message was read, so the dialog stays away as well.
  const dialogVisible = hintVisible && personSettings[ACKNOWLEDGED_KEY] !== message && !acknowledgedLocally;

  async function remember(key: typeof DISMISSED_KEY | typeof ACKNOWLEDGED_KEY): Promise<boolean> {
    const result = await updatePersonSettings({
      variables: { settings: { ...personSettings, [key]: message } },
    });
    if (result.data?.updateSettings !== true) return false;
    void refetchPersonSettings();
    return true;
  }

  async function dismissHint() {
    try {
      if (await remember(DISMISSED_KEY)) setDismissedLocally(true);
    } catch {
      // Leave the hint visible; the user can retry the dismiss.
    }
  }

  async function acknowledgeDialog() {
    try {
      await remember(ACKNOWLEDGED_KEY);
    } catch {
      // Not remembered: the dialog comes back on the next visit, which beats
      // trapping the user in it now.
    } finally {
      setAcknowledgedLocally(true);
    }
  }

  return { message, hintVisible, dialogVisible, dismissHint, acknowledgeDialog };
}
